import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { interviewNotificationService } from '../services/interviewNotificationService';

const POLL_INTERVAL_MS = 30000;
const STORAGE_KEY = 'notifiedInterviewEmails';
const ENABLED_KEY = 'interviewNotificationsEnabled';
const ALERT_ROLES = new Set(['member', 'lead']);

const getNotifiedIds = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const saveNotifiedIds = (ids) => {
  const trimmed = [...ids].slice(-500);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
};

const isEnabled = () => {
  const stored = localStorage.getItem(ENABLED_KEY);
  return stored === null ? true : JSON.parse(stored);
};

const clipText = (text, length = 140) => {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length)}…` : text;
};

const InterviewInvitationNotifier = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastCheckRef = useRef(new Date());
  const notifiedIdsRef = useRef(getNotifiedIds());

  useEffect(() => {
    if (!user || !ALERT_ROLES.has(user.role)) return;

    const showNotification = (notification) => {
      if (!isEnabled() || !('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }

      const notificationId = notification.gmailMessageId || notification.id;
      if (notifiedIdsRef.current.has(notificationId)) return;

      const handleView = () => {
        navigate('/bid-history', { state: { openMailsTab: true } });
      };

      const body = [
        notification.companyName,
        `Profile: ${notification.resumeName}`,
        clipText(notification.subject, 80),
        clipText(notification.snippet),
      ].filter(Boolean).join('\n');

      const browserNotification = new Notification('New Interview Invitation', {
        body,
        icon: '/favicon.ico',
        tag: notificationId,
        requireInteraction: true,
      });

      browserNotification.onclick = () => {
        window.focus();
        handleView();
        browserNotification.close();
      };

      notifiedIdsRef.current.add(notificationId);
      saveNotifiedIds(notifiedIdsRef.current);
    };

    const checkNotifications = async () => {
      const since = lastCheckRef.current;
      const notifications = await interviewNotificationService.getNewNotifications(since);

      notifications
        .sort((a, b) => new Date(a.processedAt) - new Date(b.processedAt))
        .forEach(showNotification);

      lastCheckRef.current = new Date();
    };

    if (isEnabled() && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    checkNotifications();
    const interval = setInterval(checkNotifications, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, navigate]);

  return null;
};

export default InterviewInvitationNotifier;
